import React from 'react';

export const API_BASE = process.env.NODE_ENV === 'production'
  ? '/api'
  : 'http://localhost:3001/api';

// SEC-25: log a failed request WITHOUT handing the console the error object.
//
// `console.error('...', err)` on an axios rejection puts the whole error in
// devtools, and axios hangs the full request config off it — including
// err.config.headers.Authorization, i.e. the live bearer token. MSAL errors
// carry token material of their own. Anyone with the console open (or a
// screen-share, or a pasted screenshot, or a devtools recording) gets a
// credential, which is exactly the disclosure this closes.
//
// So the two fields that are actually useful for debugging — the message and
// the HTTP status — are pulled out by hand and everything else is dropped.
// Deliberately NOT logged: err.config (headers), err.request, err.response.data,
// and the request URL — URLs in this app can themselves carry secrets as path
// segments (SEC-05), and the `context` string already says which call failed.
//
// Use this anywhere a caught axios/MSAL error would otherwise be logged.
export function logError(context, err) {
  const status = err?.response?.status;
  const detail = [err?.message, status ? `HTTP ${status}` : null]
    .filter(Boolean)
    .join(' — ');
  console.error(context, detail || 'unknown error');
}

// BUG-11: turn a rejected save into something the user can act on, inside the
// modal. `onSave` (App's handleSave) rejects with the axios error; everything
// rendered is derived from the RESPONSE here — never the error object itself,
// and never err.message for a request that did reach the server (SEC-25). The
// console side is logError's job.
//
// Returns { message, details, hint, gone }:
//   details  the 400 bodies from middleware/validate.js carry
//            `{ error: 'Validation failed', details: ['Contact type is required', …] }`
//            — one string per bad field. Listed, not interpolated: dropping the
//            array into JSX text is what renders "[object Object]".
//   gone     a 404, i.e. BUG-18 #7's "deleted by another user". Retrying is
//            futile, so the caller offers close-and-refresh instead of Save.
//            A 409 ("modified by another user", or a duplicate contract number)
//            is the opposite: the record is there and a retry is the fix, so it
//            keeps the normal buttons and the server's own wording.
//
// BUG-18 #26: lives here rather than in FormModal because ConvertWizard needs
// the identical mapping for its own in-modal banner. `type` is interpolated into
// the copy, so callers pass the lowercase noun the sentences should read with.
//
// BUG-18 #27 follow-up: `opts` is additive and fully defaulted, so the existing
// two-argument calls (FormModal, ConvertWizard, and every create/link site in
// DetailModal) render byte-identically to before it existed.
//   verb  the action attempted, when it is not a save. The templates need two
//         forms — the infinitive ("Could not delete this …") and the past
//         participle ("… was not deleted") — so a string supplies both via the
//         `e`-rule below, which is correct for every verb this app uses
//         (save/saved, delete/deleted, complete/completed, remove/removed,
//         update/updated, unlink/unlinked). Pass { infinitive, past } for
//         anything irregular. Note that "was deleted by another user" in the 404
//         sentence is NOT this verb: it describes what happened to the record,
//         not what the user was attempting, so it stays fixed.
//   hint  overrides the built-in hint; an explicit `hint: null` suppresses it.
//         DetailModal needs the suppression — the default hint talks about
//         closing a form and an already-refreshed list, and in that modal
//         neither is true (nothing refetches on these error paths).
export function describeSaveError(err, type, opts = {}) {
  const status = err?.response?.status;
  const data = err?.response?.data;
  const serverMessage = typeof data?.error === 'string' ? data.error : null;
  const details = Array.isArray(data?.details) ? data.details.filter(d => typeof d === 'string') : [];

  const verb = opts.verb || 'save';
  const infinitive = typeof verb === 'string' ? verb : verb.infinitive;
  const past = typeof verb === 'string'
    ? (verb.endsWith('e') ? `${verb}d` : `${verb}ed`)
    : verb.past;
  // hasOwnProperty, not a truthiness test: `hint: null` must be distinguishable
  // from "no hint option given", since null is the suppression signal.
  const hintGiven = Object.prototype.hasOwnProperty.call(opts, 'hint');
  const hint = (built) => (hintGiven ? opts.hint : built);

  if (status === 404) {
    return {
      message: `This ${type} was deleted by another user, so it can no longer be ${past}.`,
      details: [],
      hint: hint('Close the form — the list has already been refreshed. Copy anything you still need first.'),
      gone: true,
    };
  }
  // No response at all. axios sets `request` when it did send and got nothing
  // back — offline, DNS, server down — which is the only case that earns a
  // "check your connection". Anything else reaching here is a client-side fault
  // and must not be blamed on the network.
  if (!err?.response) {
    return {
      message: err?.request
        ? `Could not reach the server, so this ${type} was not ${past}. Check your connection and try again.`
        : `Could not ${infinitive} this ${type}. Please try again.`,
      details: [],
      hint: hint(null),
      gone: false,
    };
  }
  return {
    message: serverMessage || `Could not ${infinitive} this ${type}. Please try again.`,
    details,
    hint: hint(null),
    gone: false,
  };
}

// Helper to render <option> tags from dropdownOptions
export function renderOptions(options, placeholder) {
  return (
    <>
      {placeholder && <option value="">{placeholder}</option>}
      {(options || []).map(opt => (
        <option key={opt.value} value={opt.value}>{opt.label}</option>
      ))}
    </>
  );
}

// Helper: determine if an entity record is "active"
export function isEntityActive(type, record) {
  switch (type) {
    case 'contact':
      return record.Status !== 'Inactive';
    case 'property':
      return record.Status !== 'Inactive';
    case 'lead':
      return !['Closed-Won', 'Closed-Lost'].includes(record.Status);
    case 'contract':
      return !['Completed', 'Terminated', 'Expired'].includes(record.Status);
    case 'reminder':
      return record.Status !== 'Completed';
    default:
      return true;
  }
}

// Status filter toggle: Show All / Show Active / Show Inactive
export function StatusFilter({ value, onChange }) {
  const options = ['Active', 'All', 'Inactive'];
  return (
    <div className="crm-status-filter">
      {options.map(opt => (
        <button
          key={opt}
          onClick={() => onChange(opt)}
          className={`crm-status-filter__btn ${value === opt ? 'crm-status-filter__btn--active' : ''}`}
        >
          {opt}
        </button>
      ))}
    </div>
  );
}

// Tiered money formatter: $850 / $45K / $3.5M
export function formatMoney(raw) {
  const value = Number(raw) || 0;
  if (value >= 1000000) {
    const m = value / 1000000;
    return `$${m % 1 === 0 ? m.toFixed(0) : m.toFixed(1)}M`;
  }
  if (value >= 1000) return `$${(value / 1000).toFixed(0)}K`;
  return `$${value.toLocaleString()}`;
}

export function capitalizeFirst(str) {
  if (!str) return '';
  return str.charAt(0).toUpperCase() + str.slice(1);
}

// Multi-assignee picker (checkbox list). `value` is an array of user IDs;
// `onChange` receives the updated array. Reused by the reminder forms.
export function AssigneeMultiSelect({ users, value, onChange }) {
  const selected = new Set((value || []).map(Number));
  const toggle = (id) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id); else next.add(id);
    onChange(Array.from(next));
  };
  const list = users || [];
  if (list.length === 0) {
    return <p className="crm-text--muted" style={{ fontSize: '0.813rem', margin: 0 }}>No users available</p>;
  }
  return (
    <div
      className="crm-assignee-select"
      style={{ maxHeight: '132px', overflowY: 'auto', border: '1px solid var(--input-border)', borderRadius: '6px', padding: '0.35rem 0.6rem' }}
    >
      {list.map(u => (
        <label key={u.UserId} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.15rem 0', cursor: 'pointer', fontSize: '0.875rem' }}>
          <input
            type="checkbox"
            checked={selected.has(Number(u.UserId))}
            onChange={() => toggle(Number(u.UserId))}
          />
          {u.FirstName} {u.LastName}
        </label>
      ))}
    </div>
  );
}

// Parse a reminder's Assignees column (JSON string from SQL Server FOR JSON, or
// an array) into [{ UserId, Name }, ...].
export function parseAssignees(reminder) {
  if (!reminder) return [];
  const raw = reminder.Assignees;
  if (Array.isArray(raw)) return raw;
  if (typeof raw === 'string' && raw.trim()) {
    try { return JSON.parse(raw) || []; } catch { return []; }
  }
  return [];
}

// ── Date-only / time-only rendering (BUG-08) ─────────────────────────────────
//
// SQL `date` and `time` columns reach the browser as full ISO **instants**: the
// driver turns a `date` into '2026-08-14T00:00:00.000Z' (UTC midnight) and a
// `time` into '1970-01-01T08:30:00.000Z'. Handing either to `new Date(...)` and
// then to a `toLocale*String()` renders it in the VIEWER's zone, so everywhere
// west of UTC a date showed the **previous day** and a due time shifted by the
// offset (08:30 rendered as 3:30 AM in Chicago). Neither value denotes an
// instant — both are wall-clock facts — so they are formatted from the string.
//
// The `Date` these helpers build is constructed from LOCAL component integers,
// which is the inverse of the operation that caused the bug (parsing an ISO
// string as UTC): `new Date(2026, 7, 14, 12)` is midday on 14 August in every
// zone, so `toLocaleDateString()` renders 14 August in every zone. That keeps
// the app's existing locale-aware output byte-for-byte — plain string
// concatenation would hardcode US ordering — while making it zone-independent.
// Midday rather than midnight so that a DST transition at midnight (which some
// zones have) cannot roll the day.

// Format a date-only value (SQL `date`, or a 'YYYY-MM-DD' form value).
// `options` is optional Intl date options; omit it to get the plain
// `toLocaleDateString()` output every CRM screen already shows.
export function formatDateOnly(value, options) {
  if (value === null || value === undefined || value === '') return '';
  const m = String(value).match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return '';
  const local = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]), 12);
  // undefined locale === the bare toLocaleDateString() call these sites used;
  // callers that pass options (the DM module) get its existing 'en-US'.
  return local.toLocaleDateString(options ? 'en-US' : undefined, options);
}

// A reminder's DueTime arrives as the SQL `time` column serialized by the
// driver — an ISO instant on the epoch date ("1970-01-01T08:00:00.000Z").
// <input type="time"> accepts only "HH:mm", so an un-normalized value renders
// the field blank; saving then sends the raw ISO string, which the server's
// shape check rejects, and the due time is silently wiped (BUG-04). Read the
// wall-clock time out of the string itself — never via `new Date(...)`, which
// would shift it by the browser's UTC offset.
//
// BUG-08: lives here rather than in FormModal because the display helper below
// needs exactly the same extraction. Two copies of this rule were what let the
// form and the list disagree about the same value.
export function toTimeInputValue(raw) {
  if (raw === null || raw === undefined) return '';
  const s = String(raw).trim();
  if (!s) return '';
  const timePart = s.includes('T') ? s.split('T').pop() : s;
  const m = timePart.match(/^(\d{2}):(\d{2})/);
  return m ? `${m[1]}:${m[2]}` : '';
}

// Format a time-only value (SQL `time`, or an 'HH:mm' form value) for display,
// in the same shape the reminder rows already used.
export function formatTimeOnly(value) {
  const hm = toTimeInputValue(value);
  if (!hm) return '';
  const [h, m] = hm.split(':').map(Number);
  return new Date(1970, 0, 1, h, m).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

// Today's LOCAL calendar date as 'YYYY-MM-DD', for date-input defaults.
// `new Date().toISOString().split('T')[0]` is the UTC date, which after ~18:00
// Chicago is already tomorrow (BUG-08, ConvertWizard's default start date).
export function todayLocalDate() {
  const now = new Date();
  const pad = (n) => String(n).padStart(2, '0');
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
}

// Parse a lead's LinkedProperties column (JSON string from SQL Server FOR JSON,
// or an array) into [{ PropertyId, PropertyName }, ...].
export function parseLinkedProperties(lead) {
  if (!lead) return [];
  const raw = lead.LinkedProperties;
  if (Array.isArray(raw)) return raw;
  if (typeof raw === 'string' && raw.trim()) {
    try { return JSON.parse(raw) || []; } catch { return []; }
  }
  return [];
}
